import Button from "components/base/Button";
import { Input } from "components/base/Form";
import { InlineSpinner } from "components/base/Spinner";
import OdooStyleProductSelector from "components/shared/OdooStyleProductSelector";
import moment from "moment";
import { INC_PRICELIST_ID } from "pages/sales/consts";
import { useContext } from "react";
import { BiTrash } from "react-icons/bi";
import { VscSourceControl } from "react-icons/vsc";
import { PlanContext } from "./PlanForm";

const PlanLine = ({
  index,
  line,
  computeError,
  onChangeLine,
  onChangeLineProduct,
  removeLine,
  setNeedToRecompute,
  disabled,
  isComputing,
  isSaving,
  isLocking,
  showMarginCol,
  odooProductsForCost,
}) => {
  const { originalPlan, currencySymbolString, showBom } =
    useContext(PlanContext);
  const isLineEditingDisabled = isComputing || isLocking;

  const isProductValidated =
    !line.product ||
    (line.validGW &&
      line.validCbm &&
      line.inPricelist &&
      line.validCountry &&
      line.priceConfirmed &&
      line.validCPSC &&
      line.validGMARK &&
      line.validForIncOdoo &&
      line.isProductActive);

  const isLineValidated =
    isProductValidated &&
    line.validQty &&
    !line.hasDifferentPrice &&
    !computeError;

  let lineStyle = "border-y border-gray-100 dark:border-gray-700";
  if (!isLineValidated) {
    lineStyle += " bg-red-100/80 dark:bg-red-900/30";
  }

  const priceAfterDiscounnt =
    parseFloat(line.salesPrice) *
    ((100 - parseFloat(line.discountPercentage)) / 100);

  const cost = odooProductsForCost[line.product?.odooId]
    ? odooProductsForCost[line.product.odooId]
    : null;
  let margin, marginPercentage;
  if (cost) {
    margin = priceAfterDiscounnt - cost;
    marginPercentage = (margin / priceAfterDiscounnt) * 100;
  }

  const valueView = line.product ? (
    <div>
      [{line.product.number}] ({line.product.countryCodeOfOrigin}){" "}
      {line.product.name}
      {line.product.version ? ` (v${line.product.version})` : ""}
    </div>
  ) : null;

  return (
    <tr className={lineStyle}>
      <td>
        <div className="flex space-x-2 items-center">
          {disabled ? null : (
            <>
              {isSaving ? (
                <div className="px-2">
                  <InlineSpinner size={16} text={null} />
                </div>
              ) : (
                <Button
                  disabled={isLineEditingDisabled}
                  danger
                  onClick={() => {
                    removeLine(line.id);
                    if (line.qty !== 0) setNeedToRecompute(true);
                  }}
                >
                  <BiTrash />
                </Button>
              )}
            </>
          )}
          <span>{index + 1}. </span>
          <div className="w-full">
            <div className="relative w-full flex items-center space-x-2">
              {disabled ? (
                <div className="flex space-x-2 items-center">
                  [{line.product.number}] ({line.product.countryCodeOfOrigin}){" "}
                  {line.product.name}
                  {line.product.version ? ` (v${line.product.version})` : ""}
                </div>
              ) : (
                <div className="w-full">
                  <OdooStyleProductSelector
                    company={originalPlan.wisCustomer.system}
                    valueView={valueView}
                    autoFocus={line.isNew && !line.product}
                    disabled={isLineEditingDisabled}
                    onSelect={(product) => {
                      onChangeLineProduct(product);
                    }}
                    onBlur={() => {
                      if (!line.product) {
                        removeLine(line.id);
                      }
                    }}
                    priceListId={
                      originalPlan.wisPriceList?.id === `${INC_PRICELIST_ID}`
                        ? INC_PRICELIST_ID
                        : null
                    }
                    preferPriceListId={originalPlan.wisPriceList?.id}
                  />
                </div>
              )}

              {line.product ? (
                <Button
                  className="text-sm"
                  onClick={() => showBom(line.product)}
                >
                  <VscSourceControl size={16} />
                </Button>
              ) : null}
            </div>

            {line.product && !isLineValidated && (
              <div className="text-red-600 dark:text-red-700 font-semibold mx-0.5 mt-1 space-y-1">
                {!line.isProductActive && (
                  <div>⚠️ Product is inactive in the system.</div>
                )}

                {!line.validGW && (
                  <div>⚠️ Product gross weight is missing.</div>
                )}

                {!line.validCbm && <div>⚠️ Product CBM is missing.</div>}

                {line.inPricelist ? (
                  !line.priceConfirmed && (
                    <div>
                      ⚠️ Sales Price in the pricelist is not confirmed yet.
                    </div>
                  )
                ) : (
                  <div>⚠️ Product not in price list</div>
                )}

                {!line.validCountry && (
                  <div>
                    ⚠️ Product country of origin (
                    {line.product.countryCodeOfOrigin}) is not allowed to ship
                    from the warehouse.
                  </div>
                )}

                {!line.validCPSC && (
                  <div>⚠️ CPSC certificate is not valid for the product.</div>
                )}

                {!line.validGMARK && (
                  <div>⚠️ GMARK certificate is not valid for the product.</div>
                )}

                {line.hasDifferentPrice && (
                  <div>
                    ⚠️ Sales Price / Discount is different from the same product
                    pricing in the plan (including parent plan and other
                    additional plans): {line.otherPrices}.
                  </div>
                )}

                {!line.validForIncOdoo && (
                  <div>⚠️ Product is not linked to INC Odoo product.</div>
                )}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="text-right">
        {line.product ? line.product.itemsPerSet : " - "}
      </td>
      <td className="text-right">
        <div className="flex justify-end space-x-2">
          {disabled ? (
            <span className={`whitespace-nowrap`}>{line.finalQty}</span>
          ) : (
            <Input
              disabled={isLineEditingDisabled}
              validated={line.validQty}
              className="text-right w-16"
              value={line.qty}
              onChange={(e) => {
                onChangeLine(line.id, "qty", parseInt(e.target.value) || 0);
              }}
            />
          )}
        </div>
      </td>
      <td className="text-right">{line.totalCartons}</td>
      <td className="text-right">{line.totalGrossWeight?.toFixed(3)}</td>
      <td className="text-right">{line.totalCbm.toFixed(3)}</td>
      <td className={`text-right ${computeError && "text-red-600"}`}>
        {isComputing
          ? "..."
          : line.readyDate
            ? moment(line.readyDate).format("YYYY-MM-DD")
            : " - "}
      </td>
      {originalPlan.planType === "AB_INTERNAL_TRANSFER" ? (
        <>
          <td className="text-right">
            {currencySymbolString}{" "}
            {line.unitCost ? line.unitCost.toFixed(3) : "0.000"}
          </td>

          <td className="text-right">
            {currencySymbolString} {line.totalCost.toFixed(2)}
          </td>
        </>
      ) : (
        <>
          <td className="text-right">
            <div className="flex items-center justify-end space-x-2">
              {disabled ? (
                line.salesPrice
              ) : (
                <Input
                  validated={!line.hasDifferentPrice}
                  disabled={isLineEditingDisabled}
                  className={`text-right w-16`}
                  value={line.salesPrice}
                  onChange={(e) =>
                    onChangeLine(line.id, "salesPrice", e.target.value)
                  }
                />
              )}
            </div>
          </td>
          <td className="text-right">
            {disabled ? (
              line.discountPercentage
            ) : (
              <Input
                disabled={isLineEditingDisabled}
                className="text-right w-16"
                value={line.discountPercentage || 0}
                onChange={(e) =>
                  onChangeLine(line.id, "discountPercentage", e.target.value)
                }
              />
            )}
          </td>
          <td className="text-right">
            {/* Sale price after Discount */}
            {currencySymbolString}{" "}
            {priceAfterDiscounnt.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </td>
          {showMarginCol && (
            <td className="text-right">
              {margin ? (
                <span className="whitespace-nowrap">
                  {(margin * parseInt(line.qty)).toFixed(2)} (
                  {marginPercentage.toFixed(2)}%)
                </span>
              ) : (
                "-"
              )}
            </td>
          )}

          <td className="text-right">
            {currencySymbolString}{" "}
            {line.totalPrice ? line.totalPrice.toFixed(2) : "0.00"}
          </td>
        </>
      )}
    </tr>
  );
};

export default PlanLine;
